import { createClient, Client } from 'graphql-ws';
import { getJWTTokens } from '@shared/libs/local-storage';
import { getWsEndpoint } from '@shared/api/api-url';
import { ensureValidAccessToken } from '@shared/api/ws-auth';
import { FILTERS_UPDATES_SUBSCRIPTION, FiltersUpdatesPayload } from './subscription';
import { FilterProject, FiltersWsListener, FiltersWsState, FiltersWsStatus } from './types';

const INITIAL_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 15000;
const MAX_RETRY_ATTEMPTS = 5;

let projects: FilterProject[] = [];
let status: FiltersWsStatus = 'idle';
let client: Client | null = null;
let unsubscribe: (() => void) | null = null;
let retryTimeout: ReturnType<typeof setTimeout> | null = null;
let backoffMs = INITIAL_BACKOFF_MS;
let retryAttemptCount = 0;
let generation = 0;

const listeners = new Set<FiltersWsListener>();

const getState = (): FiltersWsState => ({ projects, status });

const notify = () => {
  const state = getState();
  listeners.forEach((listener) => listener(state));
};

const setStatus = (next: FiltersWsStatus) => {
  if (status === next) return;
  status = next;
  notify();
};

const resetRetryState = () => {
  retryAttemptCount = 0;
  backoffMs = INITIAL_BACKOFF_MS;
};

const setProjects = (next: FilterProject[]) => {
  projects = next;
  status = 'ready';
  resetRetryState();
  notify();
};

export const applyProjects = (next: FilterProject[]) => {
  setProjects(next);
};

export const markFiltersConnecting = () => {
  setStatus('connecting');
};

export const markFiltersError = () => {
  setStatus('error');
};

export const mapFiltersProjectsPayload = (payload: FiltersUpdatesPayload | undefined): FilterProject[] | null =>
  mapProjects(payload);

const clearRetryTimeout = () => {
  if (!retryTimeout) return;
  clearTimeout(retryTimeout);
  retryTimeout = null;
};

const disposeClient = () => {
  unsubscribe?.();
  unsubscribe = null;

  if (!client) return;

  const activeClient = client;
  client = null;
  void Promise.resolve(activeClient.dispose()).catch(() => {});
};

const mapProjects = (payload: FiltersUpdatesPayload | undefined): FilterProject[] | null => {
  const rawProjects = payload?.filtersUpdates?.projects;

  if (!Array.isArray(rawProjects)) {
    return null;
  }

  return rawProjects
    .filter((project): project is { code?: string | null; name?: string | null } => !!project)
    .map((project) => ({
      code: project.code ?? '',
      name: project.name ?? '',
    }))
    .filter((project) => project.code || project.name);
};

const isUnauthorizedError = (errors: readonly { message?: string }[] | undefined): boolean =>
  !!errors?.some((error) => error.message?.toLowerCase().includes('unauthorized'));

const isUnauthorizedTransportError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;

  const { code, reason, message } = error as { code?: unknown; reason?: unknown; message?: unknown };
  const errorText = typeof reason === 'string' ? reason : typeof message === 'string' ? message : '';

  return code === 4401 || code === 4403 || errorText.toLowerCase().includes('unauthorized');
};

const scheduleRetry = (currentGeneration: number) => {
  clearRetryTimeout();

  if (retryAttemptCount >= MAX_RETRY_ATTEMPTS) {
    setStatus('error');
    return;
  }

  retryAttemptCount += 1;

  retryTimeout = setTimeout(() => {
    retryTimeout = null;
    if (currentGeneration !== generation) return;
    backoffMs = Math.min(backoffMs * 2, MAX_BACKOFF_MS);
    void connect(currentGeneration);
  }, backoffMs);
};

const connect = async (currentGeneration: number) => {
  if (currentGeneration !== generation) return;

  clearRetryTimeout();
  disposeClient();
  setStatus('connecting');

  const accessToken = await ensureValidAccessToken();
  if (currentGeneration !== generation) return;

  if (!accessToken) {
    setStatus('error');
    return;
  }

  const wsUrl = getWsEndpoint();
  let receivedProjects = false;

  try {
    client = createClient({
      url: wsUrl,
      lazy: false,
      retryAttempts: 0,
      connectionParams: () => {
        const { accessToken: token } = getJWTTokens();
        return {
          Authorization: `Bearer ${token}`,
        };
      },
    });

    unsubscribe = client.subscribe(
      { query: FILTERS_UPDATES_SUBSCRIPTION },
      {
        next: (result) => {
          if (currentGeneration !== generation) return;

          if (result.errors?.length) {
            disposeClient();
            if (isUnauthorizedError(result.errors)) {
              setStatus('error');
              return;
            }
            scheduleRetry(currentGeneration);
            return;
          }

          const mapped = mapProjects(result.data as FiltersUpdatesPayload | undefined);
          if (!mapped) return;

          receivedProjects = true;
          setProjects(mapped);
          disposeClient();
        },
        error: (error) => {
          if (currentGeneration !== generation || receivedProjects) return;
          disposeClient();
          if (isUnauthorizedTransportError(error)) {
            setStatus('error');
            return;
          }
          scheduleRetry(currentGeneration);
        },
        complete: () => {
          if (currentGeneration !== generation || receivedProjects) return;
          disposeClient();
          scheduleRetry(currentGeneration);
        },
      }
    );
  } catch {
    disposeClient();
    scheduleRetry(currentGeneration);
  }
};

export const getProjects = (): FilterProject[] => projects;

export const getStatus = (): FiltersWsStatus => status;

export const subscribe = (listener: FiltersWsListener): (() => void) => {
  listeners.add(listener);
  listener(getState());
  return () => {
    listeners.delete(listener);
  };
};

export const closeConnection = () => {
  clearRetryTimeout();
  disposeClient();
  if (status === 'connecting' || status === 'error') {
    setStatus(projects.length ? 'ready' : 'idle');
  }
};

export const clearFilters = () => {
  generation += 1;
  clearRetryTimeout();
  disposeClient();
  projects = [];
  status = 'idle';
  resetRetryState();
  notify();
};

export const ensureFiltersLoaded = (options?: { force?: boolean }) => {
  const force = options?.force ?? false;

  if (status === 'ready') return;
  if (status === 'connecting') return;

  if (status === 'error') {
    if (!force) return;
    resetRetryState();
    status = 'idle';
  }

  const currentGeneration = generation;
  void connect(currentGeneration);
};
